import { ChangeDetectionStrategy, Component, ViewChild, OnInit, ChangeDetectorRef } from "@angular/core";
import { UniversalModalService } from "@modules/+new/shared/universal-modal/universal-modal.service";
import { ModalCloseReason } from "@modules/+new/shared/universal-modal/entities/modal-close-reason.enum";
import { CashPlusFacade } from "@modules/cashplus/+state/cashplus.facade";
import { Validateable } from "@models";
import { CashplusNavigationService } from "@modules/cashplus/infrastructure/cashplus-navigation.service";
import { map, switchMap, take, tap } from "rxjs/operators";
import { CashplusPortfolioFacade } from "@modules/cashplus/+state/cashplus-portfolio.facade";
import { EMPTY, Observable } from "rxjs";
import { CashPlusPortfolioApi } from "@modules/cashplus/entities/cashplus.model";
import { CashPlusTrackingFacade } from "@modules/cashplus/+state/cashplus-tracking.facade";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";

@Component({
	selector: "wallet-cash-out",
	templateUrl: "./wallet-cash-out.component.html",
	styleUrls: ["./wallet-cash-out.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [],
	standalone: false
})
export class WalletCashOutComponent implements OnInit {
	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;
	public cash_plus_toggle: CashPlusSlider = new CashPlusSlider();
	public cashOutViewModel$: Observable<CashOutViewModel> = EMPTY;

	public bundles: AgreementBundle[] = [];
	public dynamictermAndConditionActive = false;

	public isDynamictermAndConditionActive$ = this.termsAndConditionsService.isDynamictermAndConditionActive$.pipe(
		tap((isActive) => {
			this.dynamictermAndConditionActive = isActive;
		})
	);

	public constructor(
		private readonly modalService: UniversalModalService,
		private readonly cashPlusFacade: CashPlusFacade,
		private readonly cashPlusNavigation: CashplusNavigationService,
		private readonly cashPlusPortfolioFacade: CashplusPortfolioFacade,
		private readonly cashPlusTrackingFacade: CashPlusTrackingFacade,
		private readonly termsAndConditionsService: TermsAndConditionsService,
		private readonly cdr: ChangeDetectorRef
	) {
		this.cashPlusTrackingFacade.walletCashOutCmpViewed();
		this.cashOutViewModel$ = this.cashPlusNavigation
			.routeParamsReader()
			.pipe(switchMap((params) => this.getCashOutVm$(params.fiatPid, params.fiatId)));
	}

	ngOnInit(): void {
		this.termsAndConditionsService
			.getAgreementBundleData$([UserJourney.CashPlusBalanceMove])
			.pipe(take(1))
			.subscribe((bundles) => {
				this.bundles = bundles;
				this.cdr.markForCheck();
			});
	}

	public continue(fiatPid: string): void {
		this.cashPlusTrackingFacade.cashOutClicked();
		if (this.dynamictermAndConditionActive || this.cash_plus_toggle.enabled) {
			this.cashPlusFacade.freeUpCash(fiatPid);
			this.modalService.close(ModalCloseReason.close);
		}
		this.cash_plus_toggle.wiggle("enabled");
	}

	public cashOut(fiatPid: string): void {
		if (this.dynamictermAndConditionActive) {
			if (this.dynamicTermsInvalid) {
				this.dynamic_terms.wiggleIt();
				return;
			}
			if (this.bundles.length === 0) {
				this.cashPlusFacade.freeUpCash(fiatPid);
				this.modalService.close(ModalCloseReason.close);
				return;
			}
			this.termsAndConditionsService.acceptTerms(
				[UserJourney.CashPlusBalanceMove],
				this.dynamic_terms?.termsAndConditionsForm?.value,
				() => {
					this.continue(fiatPid);
				}
			);
		} else {
			this.continue(fiatPid);
		}
	}

	public acceptedAll(): boolean {
		// TODO: add card free up cash toggle
		const staticTermsInvalid = !this.dynamictermAndConditionActive && this.cash_plus_toggle.enabled;
		return !staticTermsInvalid && !this.dynamicTermsInvalid;
	}

	public getCashOutVm$(fiatPid: string, fiatId: string): Observable<CashOutViewModel> {
		return this.cashPlusPortfolioFacade.getPortfolioPerFiatState$(fiatPid).pipe(
			map((state) => ({
				cpPortfolioApi: state,
				fiatId: fiatId,
				fiatPid: fiatPid
			}))
		);
	}

	public get dynamicTermsInvalid(): boolean {
		const termsForm = this.dynamic_terms?.termsAndConditionsForm;

		return (
			this.bundles.length > 0 && termsForm && (Object.keys(termsForm?.value).length === 0 || !termsForm?.valid)
		);
	}
}

export class CashPlusSlider extends Validateable {
	public enabled: boolean = false;
}

export interface CashOutViewModel {
	cpPortfolioApi: CashPlusPortfolioApi;
	fiatPid: string;
	fiatId: string;
}
